<template>

    <Head :title="__('Password expired')" />

    <div class="min-h-screen flex flex-col lg:flex-row bg-white">
        <div class="relative hidden lg:flex lg:flex-1">
            <img :src="startSplash"
                 alt="Expired Splash"
                 class="h-full w-full object-cover" />

            <div class="absolute top-6 left-8 flex items-center gap-3 text-white">
                <img :src="loginLogo"
                     alt="Logo"
                     class="h-12 w-auto" />
            </div>
        </div>

        <div class="flex flex-col w-full lg:w-[450px] bg-white min-h-screen lg:min-h-auto">
            <div class="flex lg:hidden items-center justify-center pt-8">
                <img :src="loginLogo"
                     alt="Logo"
                     class="h-10 w-auto" />
            </div>

            <div class="flex-1 flex items-center justify-center px-6 py-10 lg:px-8">
                <div class="w-full max-w-md">
                    <div class="mb-8 text-center">
                        <h1 class="text-2xl font-semibold text-gray-900">{{ __('Password expired') }}</h1>
                        <p class="mt-2 text-sm text-gray-500">{{ __('Password expired. Please change password') }}</p>
                    </div>

                    <v-form name="formExpired"
                            class="space-y-5"
                            @keyup.enter="update">
                        <div v-if="props.rules">
                            <ul class="space-y-1 text-xs text-gray-600 bg-gray-50 rounded-md p-4 border border-gray-100">
                                <li v-for="(rule, index) in props.rules"
                                    :key="index"
                                    class="flex items-start gap-2">
                                    <i class="pi pi-info-circle text-gray-400 mt-0.5 flex-shrink-0"></i>
                                    <span>{{ rule }}</span>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <label for="current_password"
                                   class="block text-sm font-medium text-gray-700 mb-1">{{ __('Current password') }}</label>
                            <InputText id="current_password"
                                       type="password"
                                       size="small"
                                       class="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black-500/50"
                                       :class="{ 'border-gray-300': !form.errors.current_password, 'border-red-400': form.errors.current_password }"
                                       v-model="form.current_password"
                                       @input="form.errors.current_password = null" />
                            <div v-if="form.errors.current_password"
                                 class="mt-1 text-xs text-red-500">
                                {{ __(form.errors.current_password) }}
                            </div>
                        </div>

                        <div>
                            <label for="password"
                                   class="block text-sm font-medium text-gray-700 mb-1">{{ __('New password') }}</label>
                            <InputText id="password"
                                       type="password"
                                       size="small"
                                       class="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black-500/50"
                                       :class="{ 'border-gray-300': !form.errors.password, 'border-red-400': form.errors.password }"
                                       v-model="form.password" />
                            <div v-if="form.errors.password"
                                 class="mt-1 text-xs text-red-500">
                                {{ __(form.errors.password) }}
                            </div>
                        </div>

                        <div>
                            <label for="password_confirmation"
                                   class="block text-sm font-medium text-gray-700 mb-1">{{ __('Confirm new password') }}</label>
                            <InputText id="password_confirmation"
                                       type="password"
                                       size="small"
                                       class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black-500/50"
                                       v-model="form.password_confirmation" />
                        </div>

                        <Button @click="update"
                                :label="__('Set new password')"
                                :pt="{ icon: { class: 'mr-2 inline-block' } }"
                                icon="pi pi-sign-in"
                                class="w-full justify-center rounded-md bg-secondary-500 px-4 py-2 text-sm font-medium text-white hover:bg-secondary-700 transition-colors" />
                    </v-form>
                </div>
            </div>

            <footer class="px-4 py-4 text-sm text-gray-500">
                <div class="text-center">
                    <p class="font-light">Powered by © 4Evolve wersja 1.0</p>
                    <p class="text-xs text-gray-400 mt-1">rewizja {{ systemRevision }}</p>
                </div>
            </footer>
        </div>
    </div>
</template>

<script setup>
import { usePage } from '@inertiajs/vue3';
import { useForm } from '@inertiajs/vue3';
import { Head } from '@inertiajs/vue3';

import Button from 'primevue/button';
import InputText from 'primevue/inputtext';

import Layout from '@layouts/LayoutNoMenuNoFooter.vue';
import Image from '@helper/Image.vue';
import { __ } from '@helper/utils.js';
import { loginLogo, startSplash } from '@helper/constants.js';

defineOptions({ layout: Layout });

const props = defineProps({
    user_id: String,
    email: String,
    rules: Object,
});

const page = usePage();
const form = useForm({ current_password: '', password: '', password_confirmation: '' });
const systemRevision = page.props.systemRevision;

const update = () => {
    form.post(route('password-expired-update', { 'user_id': props.user_id, 'email': props.email }));
};
</script>

<style lang="scss" scoped></style>
